// React
import { useEffect, useRef, useCallback } from 'react'

// Interfaces
import { IRequestSaver } from './interfaces'

// Axios
import axios from 'axios'

// Lodash
import isEmpty from 'lodash/isEmpty'

// Constant
import { REQUEST } from 'constant/request.constant'

const useRequestSaver = () => {
  // Hook
  const requests = useRef<IRequestSaver[]>([])

  // Cancel all request when leaving page
  useEffect(() => {
    return () => {
      if (requests.current.length !== 0) {
        // eslint-disable-next-line
        requests.current.forEach((request: IRequestSaver) => {
          requestSaver_cancel(request.id)
        })
      }
    }

    // eslint-disable-next-line
  }, [])

  /**
   * @description Set cancel token
   *
   * @param {REQUEST} id
   * @param {any} anotherId
   *
   * @return {object} { cancelToken: string }
   */
  const requestSaver_setCancelToken = useCallback((id: REQUEST, anotherId?: any): { cancelToken: any } => {
    const axiosSource = axios.CancelToken.source()

    // Remove previous request if there's another new request
    const matchedRequest = requests.current.find((request: IRequestSaver) => {
      if (anotherId) {
        return request?.anotherId === anotherId
      } else {
        return request.id === id
      }
    })

    if (matchedRequest) {
      // Cancel request if exists
      matchedRequest.cancel()

      requests.current = requests.current.filter((request: IRequestSaver) => {
        if (anotherId) {
          return request?.anotherId === anotherId
        } else {
          return request.id !== id
        }
      })
    } else {
      // Push another cancel token
      requests.current = [...requests.current, { cancel: axiosSource.cancel, id, anotherId }]
    }

    return { cancelToken: axiosSource.token }
  }, [])

  /**
   * @description Clear previous request
   *
   * @param {REQUEST} id
   *
   * @return {void} void
   */
  const requestSaver_clearOldRequest = useCallback((id: REQUEST) => {
    requests.current = requests.current.filter((request: IRequestSaver) => request.id !== id)
  }, [])

  /**
   * @description Cancel some request
   *
   * @param {REQUEST} id
   * @param {number} anotherId
   *
   * @return {void} void
   */
  const requestSaver_cancel = useCallback(
    (id: REQUEST, anotherId?: number): void => {
      // Find the correct request match the id and cancel it
      const findCorrectRequest = (requests.current.find((request: IRequestSaver) => {
        if (anotherId) {
          return request?.anotherId === anotherId
        } else {
          return request.id === id
        }
      }) || {}) as IRequestSaver

      if (!isEmpty(findCorrectRequest)) {
        // Cancel the request
        findCorrectRequest.cancel()

        // Remove the previous cancel token if axios already canceled
        requestSaver_clearOldRequest(findCorrectRequest.id)
      }
    },
    [requestSaver_clearOldRequest],
  )

  return { requests, requestSaver_setCancelToken, requestSaver_cancel }
}

export { useRequestSaver }
